import { Box, Button, Flex, Heading, Image, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import type { ComponentProps } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { Landing, LandingOffer, LandingShow } from 'types/pricing';
import type { Service } from 'types/service';
import { getBrandColor } from 'utils/brandColor';
import { HTTP } from 'utils/http';

interface LandingAvailability {
  soldOut: boolean;
  remaining: number | null;
}

interface LandingPayload {
  landing: Landing;
  services: Record<string, Service>;
  availability: Record<string, LandingAvailability>;
}

function useLanding(slug: string | undefined) {
  return useQuery({
    queryKey: ['/landing', slug],
    queryFn: () => withAdminNonce(HTTP.get<LandingPayload>(`/landing/${slug}`)).send(),
    enabled: !!slug,
    retry: false,
    // L'état de vente bouge pendant la billetterie : on le rafraîchit.
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Ajoute le nonce REST des admins WordPress connectés : WordPress ne
 * reconnaît l'utilisateur d'un appel REST que s'il porte ce nonce. Sert à
 * l'aperçu des landings réservées aux admins.
 */
export function withAdminNonce<T extends { setHeader(name: string, value: string): unknown }>(request: T): T {
  const nonce = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.restNonce : null;
  if (nonce) request.setHeader('X-WP-Nonce', nonce);
  return request;
}

/** Contenu HTML saisi dans l'admin (déjà filtré par wp_kses_post à l'enregistrement). */
function Html({ html, ...props }: { html: string } & ComponentProps<typeof Text>) {
  return <Text {...props} dangerouslySetInnerHTML={{ __html: html }} />;
}

function formatPrice(offer: LandingOffer, service: Service | undefined): string {
  if (offer.priceOverride.trim() !== '') return offer.priceOverride;
  if (!service) return '';
  const price = service.OnlinePrice || service.Price;
  return `${Number.isInteger(price) ? price : price.toFixed(2).replace('.', ',')}€`;
}

/**
 * Landing événement (`#/event/:slug`), configurée dans l'onglet « Landing »
 * de l'admin Tarifs. Bandeau, présentation, billetterie en cartes (une par
 * show), étapes et bandeau de fin. Chaque bouton mène au checkout habituel.
 */
export function LandingScreen() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useLanding(slug);
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (isError || !data) {
    return (
      <Box textAlign="center" py={20} px={4}>
        <Heading size="md" mb={2}>
          Page introuvable
        </Heading>
        <Text>Cet événement n'est pas (ou plus) en ligne.</Text>
      </Box>
    );
  }

  const { landing, services, availability } = data;
  const accent = landing.accentColor || getBrandColor();

  const buy = (offer: LandingOffer) => {
    const service = services[offer.mindbodyId];
    if (!service) return;
    navigate(`/checkout/id/${service.Id}`, {
      state: {
        product: { ...service, type: 'service', IsIntroOffer: service.IsIntroOffer ?? false, fromLanding: true },
        from: location,
      },
    });
  };

  return (
    <Box
      className="mmLanding"
      color="#111"
      pb={{ base: 10, md: 16 }}
      // Certains thèmes (Dynamo Paris) passent le gras sur une police déjà
      // grasse (« Helvetica Neue Bold ») en plus petit : le navigateur
      // rajoute un faux gras par-dessus et le mot rétrécit. On garde la
      // taille du texte et on interdit le gras synthétique.
      sx={{
        '& strong, & b': {
          fontSize: 'inherit !important',
          fontSynthesis: 'none',
        },
      }}
    >
      <Hero landing={landing} />

      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 6 }}>
        {(landing.title || landing.intro || landing.chips.length > 0) && (
          <Stack spacing={4} pt={{ base: 8, md: 12 }} maxW="760px">
            {landing.title && (
              <Heading as="h1" fontSize={{ base: '28px', md: '40px' }} fontWeight={700} lineHeight={1.1} textTransform="none">
                {landing.title}
              </Heading>
            )}
            {landing.intro && <Html html={landing.intro} fontSize={{ base: '15px', md: '17px' }} lineHeight={1.5} />}
            {landing.chips.length > 0 && (
              <Flex gap={2} wrap="wrap" pt={1}>
                {landing.chips.map((chip) => (
                  <Box
                    key={chip.id}
                    px={3}
                    py={2}
                    fontSize="13px"
                    fontWeight={chip.highlight ? 700 : 500}
                    bg={chip.highlight ? accent : '#f2f2f2'}
                    color={chip.highlight ? '#fff' : '#111'}
                  >
                    {chip.label}
                  </Box>
                ))}
              </Flex>
            )}
          </Stack>
        )}

        {(landing.textBlocks.length > 0 || landing.sideImage) && (
          <Flex
            gap={{ base: 8, md: 12 }}
            pt={{ base: 10, md: 14 }}
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'flex-start' }}
          >
            <Stack spacing={8} flex="1">
              {landing.textBlocks.map((block) => (
                <Stack key={block.id} spacing={3}>
                  {block.badge && (
                    <Box alignSelf="flex-start" bg={accent} color="#fff" px={3} py={1.5} fontSize="14px" fontWeight={600}>
                      {block.badge}
                    </Box>
                  )}
                  <Html html={block.body} fontSize="15px" lineHeight={1.6} sx={{ p: { mb: 3 } }} />
                </Stack>
              ))}
            </Stack>
            {landing.sideImage && (
              <Box flex={{ base: 'none', md: '0 0 40%' }}>
                <Image src={landing.sideImage.url} alt="" w="100%" display="block" />
              </Box>
            )}
          </Flex>
        )}

        {landing.ticketing.shows.length > 0 && (
          <Box id="billetterie" pt={{ base: 14, md: 20 }}>
            <Stack spacing={2} textAlign="center" mb={{ base: 6, md: 10 }}>
              {landing.ticketing.title && (
                <Heading
                  as="h2"
                  fontWeight={800}
                  textTransform="uppercase"
                  letterSpacing="-0.02em"
                  // Les thèmes WordPress (Dynamo Paris) imposent taille et
                  // couleur des h2 : le grand titre rouge de la maquette doit
                  // l'emporter.
                  sx={{
                    color: `${accent} !important`,
                    fontSize: { base: '44px !important', md: '72px !important' },
                    lineHeight: '1 !important',
                    margin: '0 !important',
                  }}
                >
                  {landing.ticketing.title}
                </Heading>
              )}
              {landing.ticketing.subtitle && (
                <Text fontWeight={700} fontSize={{ base: '14px', md: '15px' }}>
                  {landing.ticketing.subtitle}
                </Text>
              )}
            </Stack>
            <SimpleGrid columns={{ base: 1, md: Math.min(3, landing.ticketing.shows.length) }} spacing={{ base: 6, md: 5 }}>
              {landing.ticketing.shows.map((show) => (
                <ShowCard
                  key={show.id}
                  show={show}
                  accent={accent}
                  services={services}
                  availability={availability}
                  ctaLabel={landing.ticketing.ctaLabel || 'Acheter'}
                  soldOutLabel={landing.ticketing.soldOutLabel || 'Complet'}
                  onBuy={buy}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}

        {landing.steps.items.length > 0 && (
          <Box pt={{ base: 14, md: 20 }}>
            {landing.steps.title && (
              <Heading as="h2" fontSize={{ base: '24px', md: '30px' }} fontWeight={700} mb={6} textTransform="none">
                {landing.steps.title}
              </Heading>
            )}
            <SimpleGrid columns={{ base: 1, md: Math.min(3, landing.steps.items.length) }} spacing={{ base: 6, md: 8 }}>
              {landing.steps.items.map((step, index) => (
                <Stack key={step.id} spacing={2}>
                  <Text color={accent} fontSize="40px" fontWeight={700} lineHeight={1}>
                    {index + 1}
                  </Text>
                  <Text fontWeight={700} fontSize="15px">
                    {step.title}
                  </Text>
                  <Html html={step.body} fontSize="14px" lineHeight={1.5} />
                </Stack>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {(landing.footer.image || landing.footer.title) && (
          <Flex direction={{ base: 'column', md: 'row' }} mt={{ base: 14, md: 20 }} bg="#f2f2f2">
            {landing.footer.image && (
              <Box flex="1">
                <Image src={landing.footer.image.url} alt="" w="100%" h="100%" objectFit="cover" display="block" />
              </Box>
            )}
            {landing.footer.title && (
              <Stack flex="1" justify="center" spacing={3} p={{ base: 6, md: 10 }}>
                {landing.footer.kicker && (
                  <Text fontSize="12px" fontWeight={700} letterSpacing="0.08em" textTransform="uppercase" color="#555">
                    {landing.footer.kicker}
                  </Text>
                )}
                <Heading
                  as="p"
                  fontSize={{ base: '30px', md: '44px' }}
                  fontWeight={800}
                  lineHeight={1.05}
                  textTransform="uppercase"
                  whiteSpace="pre-line"
                >
                  {landing.footer.title}
                </Heading>
              </Stack>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
}

function Hero({ landing }: { landing: Landing }) {
  const { image, imageMobile, heroVideoUrl } = landing.hero;
  const mobile = imageMobile ?? image;
  if (!image && !mobile && !heroVideoUrl) return null;

  return (
    <Box maxW="1200px" mx="auto" px={{ base: 0, md: 6 }}>
      {heroVideoUrl ? (
        <>
          <Box display={{ base: mobile ? 'none' : 'block', md: 'block' }}>
            <Box
              as="video"
              src={heroVideoUrl}
              autoPlay
              muted
              loop
              playsInline
              poster={image?.url}
              w="100%"
              display="block"
              aria-hidden="true"
            />
          </Box>
          {mobile && (
            <Image src={mobile.url} alt="" w="100%" display={{ base: 'block', md: 'none' }} />
          )}
        </>
      ) : (
        <>
          {mobile && <Image src={mobile.url} alt="" w="100%" display={{ base: 'block', md: 'none' }} />}
          {image && <Image src={image.url} alt="" w="100%" display={{ base: 'none', md: 'block' }} />}
        </>
      )}
    </Box>
  );
}

interface ShowCardProps {
  show: LandingShow;
  accent: string;
  services: Record<string, Service>;
  availability: Record<string, LandingAvailability>;
  ctaLabel: string;
  soldOutLabel: string;
  onBuy: (offer: LandingOffer) => void;
}

function ShowCard({ show, accent, services, availability, ctaLabel, soldOutLabel, onBuy }: ShowCardProps) {
  return (
    <Flex direction="column" bg="#f4f4f4" borderRadius="4px" overflow="hidden">
      {show.header && (
        <Box bg={accent} color="#fff" px={5} py={3} fontSize="13px" fontWeight={700} letterSpacing="0.06em" textTransform="uppercase">
          {show.header}
        </Box>
      )}
      {show.image && <Image src={show.image.url} alt={show.title} w="100%" display="block" />}
      <Stack spacing={1} px={5} pt={5} pb={2}>
        {show.kicker && (
          <Text fontSize="11px" fontWeight={700} letterSpacing="0.08em" textTransform="uppercase" color="#555">
            {show.kicker}
          </Text>
        )}
        {show.title && (
          <Text fontSize={{ base: '20px', md: '22px' }} fontWeight={500} lineHeight={1.15} textTransform="uppercase">
            {show.title}
          </Text>
        )}
      </Stack>
      <Stack spacing={0} flex="1">
        {show.offers.map((offer, index) => {
          const service = services[offer.mindbodyId];
          const state = availability[offer.mindbodyId];
          const soldOut = !service || !!state?.soldOut;
          return (
            <Stack
              key={offer.id}
              spacing={3}
              px={5}
              py={5}
              borderTop={index > 0 ? '1px solid #e2e2e2' : undefined}
            >
              {offer.badge && (
                <Box alignSelf="flex-start" bg={accent} color="#fff" px={2} py={1} fontSize="10px" fontWeight={700} letterSpacing="0.06em" textTransform="uppercase">
                  {offer.badge}
                </Box>
              )}
              <Flex justify="space-between" align="baseline" gap={3}>
                <Text fontSize="15px" fontWeight={500} textTransform="uppercase">
                  {offer.label}
                </Text>
                <Text fontSize="24px" fontWeight={500} flexShrink={0}>
                  {formatPrice(offer, service)}
                </Text>
              </Flex>
              <Button
                alignSelf="flex-start"
                minW="130px"
                borderRadius="999px"
                fontSize="13px"
                fontWeight={700}
                letterSpacing="0.06em"
                textTransform="uppercase"
                // Le thème du site (couleur d'action) et certains CSS de thème
                // WordPress repeignent les boutons : la couleur de l'événement
                // doit l'emporter, c'est une demande explicite (même coloris
                // pour tous les « Acheter »).
                sx={{
                  background: `${soldOut ? '#bdbdbd' : accent} !important`,
                  color: '#fff !important',
                  border: 'none !important',
                }}
                _hover={{ opacity: soldOut ? 1 : 0.88 }}
                _active={{ opacity: 0.8 }}
                _disabled={{ opacity: 1, cursor: 'not-allowed' }}
                isDisabled={soldOut}
                onClick={() => onBuy(offer)}
              >
                {soldOut ? soldOutLabel : ctaLabel}
              </Button>
              {offer.description && (
                <Text fontSize="12px" color="#333" lineHeight={1.45}>
                  {offer.description}
                </Text>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Flex>
  );
}
